import { Observable, combineLatest as observableCombineLatest, Subscription } from 'rxjs';
import { mergeMap, map, filter, pairwise, startWith } from 'rxjs';
import { Component, OnInit, PLATFORM_ID, ViewChild, inject, signal } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder, UntypedFormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import { StoresService } from '../../../core/services/stores.service';
import { MerchantsService } from '../../../core/services/merchants.service';
import { BsLocaleService, BsDatepickerInputDirective, BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import { isPlatformBrowser, NgTemplateOutlet, NgStyle, NgClass, AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { ClientFormComponent } from '../../../dashboard/client-form/client-form.component';
import { UpgradePlanModalComponent } from '../upgrade-plan-modal/upgrade-plan-modal.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToolsService } from '../../../core/services/tools.service';
import { UserService } from '../../../core/services/user.service';
import { CreateInvoiceModalComponent } from '../create-invoice-modal/create-invoice-modal.component';
import {
    add,
    set,
    format,
    differenceInMinutes,
    isBefore,
    isAfter,
    parse,
    isSameSecond,
    startOfDay,
    addMinutes,
    endOfDay,
    isToday,
    parseISO,
} from 'date-fns';
import { CalendarService } from '../../../dashboard/services/calendar.service';
import { ToastNotificationsService } from '../../../core/services/toast-notifications.service';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { ChangeData, NgxIntlTelInputModule } from 'untold-ngx-intl-tel-input';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SetupGuideStepNames } from '../../../dashboard/setup-guide/setup-guide.component';
import { IntlService } from '../../../core/services/intl.service';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { AlertComponent } from '../../../shared-with-client/alert/alert.component';
import { MobileDatePickerComponent } from '../../mobile-date-picker/mobile-date-picker.component';
import { DropdownInputComponent } from '../../../shared-with-client/dropdown-input/dropdown-input.component';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { ClientLoyaltyScoreComponent } from '../../client-loyalty-score/client-loyalty-score.component';
import { TypeaheadDirective } from 'ngx-bootstrap/typeahead';
import { PhoneInputComponent } from '../../../shared-with-client/phone-input/phone-input.component';
import { TextInputComponent } from '../../../shared-with-client/text-input/text-input.component';
import { SubmitBtnComponent } from '../../../shared-with-client/submit-btn/submit-btn.component';
import { DateFixPipe } from '../../../shared-with-client/pipes/date-fix.pipe';
import { BsDropdownDirective, BsDropdownToggleDirective, BsDropdownMenuDirective } from 'ngx-bootstrap/dropdown';
import { ClipboardModule } from '@angular/cdk/clipboard';
import Bugsnag from '@bugsnag/js';
import { InvoiceStatus, InvoicingService, ISaleStatus } from '../../../core/services/invoicing.service';
import { Router } from '@angular/router';
import { SalesStatusComponent } from '../../../dashboard/invoicing/sales-status/sales-status.component';
import { MerchantCurrencyPipe } from '../../pipes/merchant-currency.pipe';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { SaleDetailsComponent } from '../../../dashboard/invoicing/sale-details/sale-details.component';
import { PaymentMethodPipe } from '../../pipes/payment-method.pipe';
import { ClipboardInputComponent } from '../../clipboard-input/clipboard-input.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { DropdownInputV2Component } from '../../../shared-with-client/dropdown-input-v2/dropdown-input-v2.component';
import { RoomEquipmentBadgeComponent } from "../../../layout-components/room-equipment-badge/room-equipment-badge.component";
import { TeyaService } from '../../../core/services/teya.service';
import { Dialog } from '@angular/cdk/dialog';
import { SimplePaymentModalComponent } from '../simple-payment-modal/simple-payment-modal.component';
import { getBookingClientFormPatch, getSubscribedFormValue, getUnsubscribedPayloadValue } from './booking-client-form.util';
import { ensureBookingServicesAvailable, includeBookingServices } from './booking-services.util';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-create-booking-modal',
    templateUrl: './create-booking-modal.component.html',
    styleUrls: ['./create-booking-modal.component.scss'],
    imports: [
    LoaderComponent,
    FormsModule,
    ReactiveFormsModule,
    AlertComponent,
    BsDatepickerInputDirective,
    BsDatepickerDirective,
    NgxIntlTelInputModule,
    NgTemplateOutlet,
    MobileDatePickerComponent,
    NgStyle,
    DropdownInputComponent,
    TooltipDirective,
    ClientLoyaltyScoreComponent,
    NgClass,
    TypeaheadDirective,
    PhoneInputComponent,
    TextInputComponent,
    SubmitBtnComponent,
    AsyncPipe,
    CurrencyPipe,
    DatePipe,
    DateFixPipe,
    TranslatePipe,
    BsDropdownDirective,
    BsDropdownToggleDirective,
    BsDropdownMenuDirective,
    ClipboardModule,
    SalesStatusComponent,
    MerchantCurrencyPipe,
    ButtonComponent,
    PaymentMethodPipe,
    ClipboardInputComponent,
    DropdownInputV2Component,
    RoomEquipmentBadgeComponent
],
    standalone: true,
})
export class CreateBookingModalComponent implements OnInit {
    private readonly fb = inject(UntypedFormBuilder);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly bsModalRef = inject(BsModalRef);
    private readonly datePickerLocaleService = inject(BsLocaleService);
    private readonly storesService = inject(StoresService);
    private readonly merchantsService = inject(MerchantsService);
    private readonly modalService = inject(BsModalService);
    private readonly translate = inject(TranslateService);
    public readonly toolsService = inject(ToolsService);
    private readonly userService = inject(UserService);
    private readonly analytics = inject(AnalyticsService);
    private readonly calendarService = inject(CalendarService);
    private readonly toastNotificationsService = inject(ToastNotificationsService);
    public readonly intlService = inject(IntlService);
    private readonly invoicingService = inject(InvoicingService);
    private readonly router = inject(Router);
    private readonly teyaService = inject(TeyaService);
    private readonly _datePickerLocaleInitialized = this.datePickerLocaleService.use(this.intlService.getCurrentLanguage());

    public dayPickerMobile = false;
    public recurrentUntilPickerMobile = false;

    public initialRequests;

    public event;
    public fetchFullBooking = false;
    public merchant;
    public user;
    public currentMerchant;

    public currentUserObservable: Observable<any>;
    public form: UntypedFormGroup;
    public loading = true;
    public day;
    public providers;
    public defaultProviderId = null;
    public defaultRoomEquipmentId = null;
    public defaultStartHour = null;
    public defaultStartMinutes = null;
    public storeServices;
    public store;
    public currentWorkplanOverrides;
    public openTimes;

    public availableHours;
    public availableMinutes;

    public existingClient;

    public typeaheadLoading;
    public searchClients: Observable<any> = new Observable<string>((observer) => {
        observer.next(this.form.get('client_name').value);
    }).pipe(
        filter((token) => Boolean(token)),
        mergeMap((token: string) => this.doSearchClients(token)),
    );

    public saving;
    public submitAttempted = false;
    public errorMsg;

    public editClientModal: BsModalRef;
    public upgradeModal: BsModalRef;
    public invoiceModal: BsModalRef;
    public notifyUserModalRef: BsModalRef;

    public durationOptions;

    public merchantSubscription: Subscription;
    public missingPaymentCents;

    // public deleteModalRef: BsModalRef;
    public bsConfig = {
        dateInputFormat: 'DD/MM/YYYY',
        showWeekNumbers: false,
        adaptivePosition: true,
    };

    public minDate = new Date();

    public readOnly = false;

    public isBrowser;

    public recurrentOptions;

    public isRecurrent = false;
    public sendEmail = true;

    public flexibleHours = 2;
    public flexibleMinutes = 120;
    public firstTimeLoadedDatePicker = true;

    public previewMode = false;

    get showContactDetails(): boolean {
        return this.userService.canShowClientContacts(this.store);
    }

    public showAdvancedFields = false;

    public merchantCustomFields;
    public timeFormatShort;
    public dateTimeFormatShort: string;
    public totalAmountPaidCents;
    public totalPriceCents;

    public isSafariBefore17 = false;

    public bookingSale = signal<any>(null);
    public loadingSales = signal<boolean>(true);

    /** Pre-payment URL field starts hidden; revealed after clicking the label row (Figma 8775-6590 → 8775-6607). */
    public prePaymentLinkExpanded = false;

    public ISaleStatus = ISaleStatus;
    public InvoiceStatus = InvoiceStatus;
    public currentLocale = this.translate.currentLang;

    public hasLegacyPosIntegration = toSignal(this.storesService.hasLegacyPosIntegration());
    public isInvoicingActive = toSignal(this.storesService.isInvoicingActive());
    public currentStore = toSignal(this.storesService.getActiveStore());
    public teyaConnectionStatus = toSignal(this.teyaService.checkOrFetchConnectionStatus(this.currentStore()?.id));

    public storeRoomEquipments = [];

    private dialog = inject(Dialog);

    get servicesFormArray(): UntypedFormArray {
        return this.form.get('services') as UntypedFormArray;
    }

    get customFieldsControls() {
        return this.form.get('customFields') as UntypedFormArray;
    }

    canShowSendPaymentLinkOption(): boolean {
        if (!this.store || this.event) {
            return false;
        }
        return this.storesService.isPaymentsActive(this.store);
    }

    isPreviewPaymentsCardEligible(): boolean {
        const sale = this.bookingSale();
        return (
            this.previewMode &&
            !!this.store &&
            this.storesService.isPaymentsActive(this.store) &&
            !!sale &&
            sale.missingPaymentCents > 0
        );
    }

    showSalePaymentsCard(): boolean {
        const sale = this.bookingSale();
        if (!sale) {
            return false;
        }
        if (this.store.salesModuleActive && sale.status) {
            return true;
        }
        if (!this.store.salesModuleActive && sale.paidAmount > 0) {
            return true;
        }
        if (this.isPreviewPaymentsCardEligible() && sale.status === ISaleStatus.PENDING_PAYMENT) {
            return true;
        }
        return false;
    }

    showPrePaymentLinkRow(): boolean {
        const sale = this.bookingSale();
        if (!sale || !this.isPreviewPaymentsCardEligible() || sale.status !== ISaleStatus.PENDING_PAYMENT) {
            return false;
        }
        if (sale.paidAmount > 0) {
            return false;
        }
        if (sale.salePayments?.length) {
            return false;
        }
        if (sale.missingPaymentCents <= 0) {
            return false;
        }
        return Boolean(this.getClientBookingPayUrl());
    }

    getClientBookingPayUrl(): string {
        if (!this.isBrowser || !this.event || !this.merchant?.slug) {
            return '';
        }

        const parentBookingId = this.event.bookingPackageId ?? this.event.id;
        const bookingCode = this.getPackageParentBookingCode();

        if (!bookingCode) {
            return '';
        }

        const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
        const host = this.intlService.getHost();
        if (!host) {
            return '';
        }

        return `${protocol}//${host}/${this.merchant.slug}/pay/${parentBookingId}/${bookingCode}`;
    }

    private getPackageParentBookingCode(): string | null {
        if (!this.event?.bookingCode) {
            return null;
        }

        if (!this.event.bookingPackageId || this.event.id === this.event.bookingPackageId) {
            return this.event.bookingCode;
        }

        const parentBooking = this.event.multipleBookings?.find(
            (booking) => booking.id === this.event.bookingPackageId,
        );

        return parentBooking?.bookingCode ?? this.event.multipleBookings?.[0]?.bookingCode ?? null;
    }

    togglePrePaymentLinkExpanded(): void {
        this.prePaymentLinkExpanded = !this.prePaymentLinkExpanded;
    }

    private syncSendPaymentLinkCheckState(): void {
        if (!this.form?.get('sendPaymentLinkCheck')) {
            return;
        }
        if (!this.canShowSendPaymentLinkOption()) {
            this.form.patchValue({ sendPaymentLinkCheck: false }, { emitEvent: false });
        }
    }

    ngOnInit() {
        this.initializeModal();
    }

    initializeModal() {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.timeFormatShort = this.intlService.getTimeFormatShort();
        this.dateTimeFormatShort = this.intlService.getDateTimeFormatShort(true);

        if (this.isBrowser) {
            this.isSafariBefore17 = this.toolsService.isSafariBefore17();
        }

        this.merchantSubscription = this.merchantsService.activeMerchant.pipe(filter((v) => v)).subscribe((res) => {
            this.currentMerchant = res;
        });

        this.currentUserObservable = this.userService.currentUser;

        this.buildRecurrentOptions();

        this.durationOptions = this.toolsService.availableServiceDurations.map((v) => v.string);

        if (this.event && this.fetchFullBooking) {
            this.loading = true;
            this.calendarService.getBooking(this.event.id).subscribe({
                next: (booking) => {
                    this.event = booking;
                    this.initEditBooking();
                },
                error: () => {
                    this.toastNotificationsService.showToast(
                        this.translate.instant('GENERAL.generalErrorMsg'),
                        'error',
                    );
                    this.bsModalRef.hide();
                },
            });
            return;
        }

        if (this.event) {
            this.initEditBooking();
        } else {
            this.getInitialData();
        }
    }

    private initEditBooking() {
        this.previewMode = true;
        this.getBookingSales();
        this.getInitialData();
    }

    getBookingSales() {
        this.loadingSales.set(false);
        if (this.store.salesModuleActive && !this.canViewSaleDetails()) {
            return;
        }
        if (!this.store.salesModuleActive && this.user?.role === 'provider') {
            return;
        }

        this.loadingSales.set(true);
        this.invoicingService.getBookingSales(this.event.id, this.store?.id).subscribe({
            next: (res: any) => {
                this.bookingSale.set(res);
                this.prePaymentLinkExpanded = false;
                this.loadingSales.set(false);
            },
            error: () => {
                this.prePaymentLinkExpanded = false;
                this.loadingSales.set(false);
            },
        });
    }

    getBookingSourceCopy() {
        if (this.event?.creatorUserId) {
            return this.translate.instant('SHARED.createBookingModal.source.backOffice');
        }

        if (this.event?.source === 'googleReserve') {
            return this.translate.instant('SHARED.createBookingModal.source.googleReserve');
        }

        return this.translate.instant('SHARED.createBookingModal.source.bookingPage');
    }

    getInitialData() {
        if (!this.store?.id) {
            // send to bugsnag
            Bugsnag.notify(new Error('CreateBookingModal: Store ID is missing'));
        }

        const initialDataRequests = [
            this.storesService.getStoreServices(this.store?.id),
            this.storesService.getStoreProviders(this.store?.id),
            this.userService.currentUser,
            this.merchantsService.getMerchantCustomFields(this.currentMerchant.slug),
            this.storesService.checkOrFetchStoreRoomEquipments(this.store?.id),
        ];

        if (this.event?.client?.id) {
            initialDataRequests.push(this.merchantsService.getClientCustomFields(this.event.client.id));
        }

        this.calculateBookingsAmounts();

        this.initialRequests = observableCombineLatest(initialDataRequests);
        this.initialRequests.subscribe((res) => {
            const storeServices = includeBookingServices(res[0], this.event);
            if (this.isSafariBefore17) {
                this.storeServices = storeServices
                    .filter((i) => !i.category)
                    .map((v) => {
                        v.name = this.getServiceNameWithCategory(v);
                        return v;
                    });
            } else {
                this.storeServices = storeServices;
            }
            this.providers = res[1];
            this.user = res[2];
            this.merchantCustomFields = res[3];

            if (this.event?.client?.id) {
                this.event.client.customFields = res?.[5];
            }

            this.storeRoomEquipments = res?.[4];

            this.generateForm(this.event);
            this.loading = false;
        });
    }

    calculateBookingsAmounts() {
        if (this.event) {
        const bookings = this.event?.multipleBookings?.length ? this.event.multipleBookings : [this.event];

        this.totalAmountPaidCents = bookings[0].totalAmountPaidCents;
        this.totalPriceCents = Math.round(this.getMultiBookingTotal(bookings) * 100);
        this.missingPaymentCents = this.totalPriceCents - this.totalAmountPaidCents;
        }
    }

    getMultiBookingTotal(bookings) {
        return bookings?.reduce((a, b) => a + b?.service?.price, 0);
    }

    doSearchClients(query) {
        const payload = {
            name: query,
            storeId: this.store?.id,
            includeCustomFields: true,
        };
        this.loading = false;
        if (this.merchant) {
            return this.merchantsService.searchClients(this.merchant?.id, payload).pipe(
                map((res) => {
                    return res.map((v) => ({ ...v, fullName: `${v.firstName} ${v.lastName ?? ''}` }));
                }),
            );
        }
        return [];
    }

    typeaheadOnSelect(event) {
        this.form.patchValue(getBookingClientFormPatch(event?.item, this.getCustomFieldsFormValue(event?.item)));

        this.form.updateValueAndValidity();
        this.form.controls['email'].disable();
        this.form.controls['phoneNumber'].disable();
        this.form.controls['vatNumber'].disable();
        this.form.controls['dob'].disable();
        this.form.controls['subscribed'].disable();
        this.form.controls['customFields'].disable();

        this.existingClient = event.item;
    }

    getCustomFieldsFormValue(client) {
        const customFieldsPatchValue = [];
        // iterate over merchant custom fields and get value from client
        for (const field of this.merchantCustomFields) {
            if (field.key) {
                // if exists key, get value from client
                customFieldsPatchValue.push({ ['_' + field.id]: client[field.key] || null });
            } else {
                customFieldsPatchValue.push({ ['_' + field.id]: client.customFields?.[field.id] || null });
            }
        }

        return customFieldsPatchValue;
    }

    clearCustomFieldsFormValue() {
        const customFieldsPatchValue = [];
        // iterate over merchant custom fields and get value from client
        for (const field of this.merchantCustomFields) {
            customFieldsPatchValue.push({ ['_' + field.id]: null });
        }

        return customFieldsPatchValue;
    }

    changeTypehead(e?) {
        if (this.form.controls['email'].disabled) {
            this.form.controls['email'].enable();
            this.form.controls['phoneNumber'].enable();
            this.form.controls['vatNumber'].enable();
            this.form.controls['dob'].enable();
            this.form.controls['subscribed'].enable();
            this.form.controls['customFields'].enable();
            this.form.patchValue({
                email: '',
                phoneNumber: '',
                vatNumber: null,
                dob: null,
                subscribed: false,
                customFields: this.clearCustomFieldsFormValue(),
            });
            this.existingClient = null;
            this.form.updateValueAndValidity();
        }
    }

    parseFieldToPhoneNumber(value: ChangeData): string {
        return value?.e164Number ?? null;
    }

    showNewClientWarning(): boolean {
        const clientName = this.form?.get('client_name')?.value?.trim();

        return !!clientName && !this.existingClient;
    }

    generateForm(data = null) {
        if (!data) {
            if (this.existingClient) {
                data = {};
                data.client = this.existingClient;
            }
        }
        this.openTimes = this.generateAvailableTimesForSelectedDay(true);
        this.availableHours = [
            '00',
            '01',
            '02',
            '03',
            '04',
            '05',
            '06',
            '07',
            '08',
            '09',
            '10',
            '11',
            '12',
            '13',
            '14',
            '15',
            '16',
            '17',
            '18',
            '19',
            '20',
            '21',
            '22',
            '23',
        ];

        this.availableHours = this.availableHours.map((t) => format(parse(t, 'HH', new Date()), this.intlService.getTimeFormatHour()));
        this.availableMinutes = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

        let fullName = '';

        if (data && data.client) {
            fullName = data.client.firstName;
            if (data?.client?.lastName) {
                fullName += ' ' + data.client.lastName;
            }
        }

        let defaultDay = new Date();
        if (this.day) {
            defaultDay = parse(this.day, 'yyyy-MM-dd', new Date());
        }

        let sendEmail;
        try {
            sendEmail = localStorage?.getItem('book:settings:sendEmail');
            this.sendEmail = JSON.parse(sendEmail).value;
        } catch (error) {
            // console.log(error);
        }

        let services = this.fb.array([]);
        if (data?.multipleBookings?.length) {
            data?.multipleBookings.forEach((data) => {
                const service = this.getServiceFormGroup(data);
                services.push(service);
            });
        } else {
            const service = this.getServiceFormGroup(data);
            services.push(service);
        }

        // custom fields logic
        const customFields = this.fb.array([]);
        if (this.merchantCustomFields?.length) {
            this.merchantCustomFields.forEach((field) => {
                let value = null;
                if (field.key) {
                    value = data?.client?.[field.key] || null;
                } else {
                    value = data?.client?.customFields?.[field.id] || null;
                }

                const control = this.fb.group({
                    ['_' + field.id]: [value, []],
                });
                customFields.push(control);
            });
        }

        // New clients default to unsubscribed; known clients share getSubscribedFormValue with the client-modal patch.
        const subscribed = data?.client ? getSubscribedFormValue(data.client) : false;

        this.form = this.fb.group({
            day: [(data?.dateInit && parse(data.dateInit, 'yyyy-MM-dd HH:mm', new Date())) || defaultDay, data ? [Validators.required] : []],
            client_name: [fullName || '', [Validators.required, Validators.maxLength(255)]],
            phoneNumber: [(data && data.client && data.client.phoneNumber) ?? ''],
            email: [(data && data.client && data.client.email) || '', [Validators.email]],
            vatNumber: [(data && data.client && data.client.vatNumber) || null, []],
            subscribed: [subscribed, []],
            dob: [(data?.client?.dob && parseISO(data.client.dob)) || null, []],
            services: services,
            notes: [(data && data.notes) || '', []],
            recurrent: [this.recurrentOptions[0].name, []],
            recurrentUntil: [null, []],
            recurrentCheck: [false, []],
            sendEmailCheck: [this.sendEmail, []],
            sendPaymentLinkCheck: [false, []],
            customFields,
        });

        if (data && data.client && data.client.id) {
            this.form.controls['email'].disable();
            this.form.controls['phoneNumber'].disable();
            this.form.controls['vatNumber'].disable();
            this.form.controls['subscribed'].disable();
            this.form.controls['dob'].disable();
            this.form.controls['customFields'].disable();
            this.form.updateValueAndValidity();
            this.existingClient = data.client;
            if (!this.showContactDetails) {
                this.form.controls['client_name'].disable();
            }
        }

        this.buildRecurrentOptions();

        this.form.get('day').valueChanges.subscribe((v) => {
            this.form.get('services').value.forEach((value, index) => {
                this.checkBookingAvailability(value, index);
            });
            this.buildRecurrentOptions(true);
        });

        this.form
            .get('services')
            .valueChanges.pipe(startWith(this.form.get('services').value), pairwise())
            .subscribe(([oldArray, newArray]) => {
                newArray.forEach((newValue, index) => {
                    if (
                        oldArray?.[index]?.duration !== newValue?.duration ||
                        oldArray?.[index]?.startHour !== newValue?.startHour ||
                        oldArray?.[index]?.startMinutes !== newValue?.startMinutes ||
                        oldArray?.[index]?.provider !== newValue?.provider ||
                        oldArray?.[index]?.roomEquipment?.id !== newValue?.roomEquipment?.id
                    ) {
                        this.checkBookingAvailability(newValue, index);
                    }
                    if (oldArray?.[index]?.provider !== newArray?.[index]?.provider) {
                        const services = this.getAvailableServicesForGroup(newArray?.[index]?.provider);
                        this.servicesFormArray.at(index).get('availableServices').patchValue(services);
                        const foundService = this._getServiceFromServiceName(newArray?.[index]?.service, services);
                        if (!foundService) {
                            this.servicesFormArray.at(index).get('service').patchValue(null);
                        }
                    }
                });

                if (newArray?.length > 1) {
                    this.form.get('recurrentCheck').patchValue(false);
                    this.isRecurrent = false;
                    this.form.get('recurrentCheck').disable();
                } else {
                    this.form.get('recurrentCheck').enable();
                }
                this.recurrentValidation();
                this.syncSendPaymentLinkCheckState();
            });

        this.form.get('sendEmailCheck').valueChanges.subscribe((sendEmailOn) => {
            if (!sendEmailOn) {
                this.form.patchValue({ sendPaymentLinkCheck: false }, { emitEvent: false });
            }
            this.syncSendPaymentLinkCheckState();
        });
    }

    checkBookingAvailability(data, index) {
        if (!data.provider || !data.duration) {
            return;
        }
        const day = (this.form.get('day').value && format(new Date(this.form.get('day').value), 'yyyy-MM-dd')) || this.day;
        let duration = null;
        if (data.duration) {
            duration = this.toolsService.parseServiceDurationToInt(data.duration);
        }

        const payload = {
            serviceId: this.getServiceFromServiceName(data?.service)?.id,
            providerId: this.getIdFromProviderName(data?.provider),
            duration,
            dateInit: this.getDateInit(day, data.startHour, data.startMinutes),
            providerSelected: data.providerSelected,
            roomEquipmentId: data?.roomEquipment?.id,
        };
        if (data?.id) {
            payload['bookingId'] = data?.id;
        }
        this.storesService.getBookingAvailabilityAdmin(this.store?.id, payload).subscribe((res) => {
            const available = res.available;
            this.servicesFormArray.at(index).get('availableWarning').patchValue(!available && res.reason === 'PROVIDER_BUSY');
            this.servicesFormArray.at(index).get('roomEquipmentAvailableWarning').patchValue(!available && res.reason === 'ROOM_EQUIPMENT_BUSY');
        });
    }

    getServiceFormGroup(data): UntypedFormGroup {
        // default hour to be the next hour
        const { defaultStartHour, defaultStartMinutes } = this.getDefaultHoursAndMinutes();

        const providerName = (data && data.provider && data.provider.name) || this.checkDefaultProvider();
        const availableServices = this.getAvailableServicesForGroup(providerName);

        let availableRoomEquipments;
        if (this.defaultRoomEquipmentId) {
            // When clicking on an empty equipment slot
            availableRoomEquipments = this.storeRoomEquipments;
        } else {
            availableRoomEquipments = this.getAvailableRoomEquipmentsForGroup(data?.service);
        }

        let defaultDuration = null;
        if (data && data.dateInit && data.dateEnd) {
            defaultDuration = differenceInMinutes(
                parse(data.dateEnd, 'yyyy-MM-dd HH:mm', new Date()),
                parse(data.dateInit, 'yyyy-MM-dd HH:mm', new Date()),
            );
        } else {
            if (this.checkDefaultService(availableServices)) {
                defaultDuration = availableServices[0].duration;
            }
        }

        let roomEquipment = null;
        if (data?.roomEquipment?.id || data?.roomEquipmentId) {
            const roomEquipmentId = data?.roomEquipment?.id ?? data.roomEquipmentId;
            roomEquipment = this.storeRoomEquipments.find((re) => re.id === roomEquipmentId) || null;
        }
        if (this.defaultRoomEquipmentId) {
            // When clicking on an empty equipment slot
            roomEquipment = this.storeRoomEquipments.find((re) => re.id === this.defaultRoomEquipmentId) || null;
        }

        const formGroup = this.fb.group({
            duration: [(defaultDuration && this.toolsService.parseServiceDurationToString(defaultDuration)) || null, []],
            startHour: [
                (data && data.dateInit && format(parse(data.dateInit, 'yyyy-MM-dd HH:mm', new Date()), this.intlService.getTimeFormatHour())) ||
                    defaultStartHour,
                [Validators.required],
            ],
            startMinutes: [
                (data && data.dateInit && format(parse(data.dateInit, 'yyyy-MM-dd HH:mm', new Date()), 'mm')) || defaultStartMinutes,
                [Validators.required],
            ],
            service: [
                (data && data.service && data.service.name && this.getServiceNameWithCategory(data.service)) ||
                    this.checkDefaultService(availableServices),
                [Validators.required],
            ],
            provider: [providerName, [Validators.required]],
            providerSelected: [(data && data.providerSelected) || false, []],
            availableWarning: [false],
            roomEquipmentAvailableWarning: [false],
            availableServices: [availableServices],
            availableRoomEquipments: [availableRoomEquipments],
            roomEquipment: [roomEquipment, []],
        });

        if (data?.id) {
            formGroup.addControl('id', this.fb.control(data.id));
        }

        return formGroup;
    }

    getAvailableServicesForGroup(providerName) {
        let services;
        if (providerName) {
            services = (this.providers && this.providers.find((v) => v.name === providerName)?.services) ?? this.storeServices;
        } else {
            services = this.storeServices;
        }
        // fix problem with category names in service from store and not provider
        if (this.isSafariBefore17) {
            services = this.storeServices && this.storeServices.filter((v) => !v.category).filter((v) => services.find((a) => a.id === v.id));
        } else {
            services = this.storeServices && this.storeServices.filter((v) => services.find((a) => a.id === v.id));
        }
        return ensureBookingServicesAvailable(services, this.storeServices, this.event);
    }

    getAvailableRoomEquipmentsForGroup(service) {
        let roomEquipments = [];
        if (service) {
            roomEquipments = this.storeRoomEquipments.filter((re) => re.services?.find((s) => s.id === service.id));
        }
        return roomEquipments;
    }

    updateDurationFromProvider(index, providerName, serviceName) {
        if (providerName) {
            const duration = this.getServiceDurationFromProviderAndServiceName(index, providerName, serviceName);
            if (duration) {
                this.servicesFormArray.at(index).get('duration').patchValue(duration);
            }
        }
    }

    updateDurationFromService(index, providerName, serviceName) {
        if (serviceName) {
            const duration = this.getServiceDurationFromProviderAndServiceName(index, providerName, serviceName);
            if (duration) {
                this.servicesFormArray.at(index).get('duration').patchValue(duration);
            }
        }
    }

    updateRoomEquipmentOptionsFromService(index, serviceName) {
        const availableRoomEquipments = this.getAvailableRoomEquipmentsForGroup(this.getServiceFromServiceName(serviceName));
        this.servicesFormArray.at(index).get('availableRoomEquipments').patchValue(availableRoomEquipments);
        const currentRoomEquipment = this.servicesFormArray.at(index).get('roomEquipment').value;
        if (currentRoomEquipment && !availableRoomEquipments?.find((re) => re.id === currentRoomEquipment.id)) {
            this.servicesFormArray.at(index).get('roomEquipment').patchValue(null);
        }
    }

    getServiceNameWithCategory(service) {
        if (this.isSafariBefore17) {
            return service.name + (service.categoryName ? ` / ${service.categoryName}` : '');
        }

        return `${service.name}${service.id}`;
    }

    getServiceDurationFromProviderAndServiceName(index, providerName, serviceName) {
        const service = this.getServiceFromServiceName(serviceName);

        const provider = this.getProviderFromProviderName(providerName);

        const serviceInProvider = provider?.services?.find((s) => s.id === service?.id);
        let duration;
        if (serviceInProvider?.durationForProvider) {
            duration = serviceInProvider?.durationForProvider;
        } else if (service?.durationForProvider) {
            duration = service?.durationForProvider;
        } else if (serviceInProvider?.duration) {
            duration = serviceInProvider?.duration;
        } else if (service?.duration) {
            duration = service?.duration;
        }
        return duration ? this.toolsService.parseServiceDurationToString(duration) : null;
    }

    getDefaultHoursAndMinutes() {
        let defaultStartHour, defaultStartMinutes;
        if (this.defaultStartHour && this.defaultStartMinutes) {
            defaultStartHour = format(parse(this.defaultStartHour, 'HH', new Date()), this.intlService.getTimeFormatHour());
            defaultStartMinutes = this.defaultStartMinutes;
        } else {
            defaultStartHour = format(add(set(new Date(), { minutes: 0 }), { hours: 1 }), this.intlService.getTimeFormatHour());
            defaultStartMinutes = format(add(set(new Date(), { minutes: 0 }), { hours: 1 }), 'mm');
        }

        return { defaultStartHour, defaultStartMinutes };
    }

    checkDefaultProvider() {
        if (this.defaultProviderId) {
            return this.providers.filter((provider) => provider.id === this.defaultProviderId)[0]?.name;
        }
        if (this.providers?.length === 1) {
            return this.providers[0].name;
        }
        return null;
    }

    checkDefaultService(availableServices) {
        if (availableServices?.length === 1) {
            return availableServices && availableServices[0].name && this.getServiceNameWithCategory(availableServices[0]);
        }
        return null;
    }

    generateAvailableTimesForSelectedDay(flexibleHours = true) {
        const bookingFrequency = this.storesService.getBookingFrequencyForCalendar(this.store.bookingFrequency);

        return this.calendarService.generateAvailableTimesForSelectedDay(
            flexibleHours,
            bookingFrequency,
            this.day,
            this.store,
            this.flexibleHours,
            this.flexibleMinutes,
            this.event,
        );
    }

    getIdFromProviderName(name) {
        return this.providers.find((v) => v.name === name)?.id;
    }

    getServiceFromServiceName(name) {
        return this._getServiceFromServiceName(name, this.storeServices);
    }

    _getServiceFromServiceName(name, services) {
        if (this.isSafariBefore17) {
            return services.find((v) => v.name === name);
        }
        return services.find((v) => `${v.name}${v.id}` === name && !v.category);
    }

    getProviderFromProviderName(name) {
        return this.providers.find((v) => v.name === name);
    }

    editClient() {
        const initialState = {
            client: this.existingClient,
            store: this.store,
            showForm: true,
            openedFromBooking: true,
        };

        this.editClientModal = this.modalService.show(ClientFormComponent, { initialState, class: 'second modal-lg' });

        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            // console.log(reason);
            if (reason && reason === 'delete-client') {
                this.form.patchValue({
                    client_name: '',
                });
                this.changeTypehead();
            }
            if (reason && typeof reason === 'object' && reason!['firstName']) {
                this.existingClient = reason;
                this.form.patchValue(getBookingClientFormPatch(reason, this.getCustomFieldsFormValue(reason)));
            }
            modalCallback.unsubscribe();
        });
    }

    toggleProviderSelected(index, selected) {
        this.servicesFormArray.at(index).patchValue({ providerSelected: selected ? false : true });
    }

    removeService(index) {
        this.servicesFormArray.removeAt(index);
    }

    addService() {
        if (this.isFreePlan()) {
            const initialState = {
                reason: 'multiBooking',
            };
            this.upgradeModal = this.modalService.show(UpgradePlanModalComponent, { class: 'modal-md second', initialState });
            const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
                if (reason && reason === 'upgrade') {
                    this.close();
                }
                modalCallback.unsubscribe();
            });
            return;
        }

        const day = (this.form.get('day').value && format(new Date(this.form.get('day').value), 'yyyy-MM-dd')) || this.day;
        let { defaultStartHour, defaultStartMinutes } = this.getDefaultHoursAndMinutes();

        const otherServicesLength = this.servicesFormArray.length;
        const duration = this.toolsService.parseServiceDurationToInt(this.servicesFormArray.at(otherServicesLength - 1).get('duration')?.value);

        const startHour = this.servicesFormArray.at(otherServicesLength - 1).get('startHour')?.value;
        const startMinutes = this.servicesFormArray.at(otherServicesLength - 1).get('startMinutes')?.value;

        if (startHour && startMinutes && duration) {
            const timeFormat = this.intlService.getTimeFormatHour();
            const date = addMinutes(parse(day + ' ' + startHour + ':' + startMinutes, `yyyy-MM-dd ${timeFormat}:mm`, new Date()), duration);

            defaultStartHour = format(date, timeFormat);
            defaultStartMinutes = format(date, 'mm');
        }

        const providerName = this.checkDefaultProvider();
        const availableServices = this.getAvailableServicesForGroup(providerName);

        const service = this.fb.group({
            duration: [null, []],
            service: [this.checkDefaultService(availableServices), [Validators.required]],
            provider: [this.checkDefaultProvider(), [Validators.required]],
            startHour: [defaultStartHour, [Validators.required]],
            startMinutes: [defaultStartMinutes, [Validators.required]],
            providerSelected: false,
            availableWarning: false,
            roomEquipmentAvailableWarning: false,
            availableServices: [availableServices],
            availableRoomEquipments: [[]],
            roomEquipment: [null, []],
        });
        this.servicesFormArray.push(service);

        this.analytics.trackEvent('addServiceInBookingModal', 'merchant');
    }

    getDateInit(day, hours, minutes) {
        return day + ' ' + format(parse(hours, this.intlService.getTimeFormatHour(), new Date()), 'HH') + ':' + minutes;
    }

    getBookingToPayload(data) {
        const day = (this.form.get('day').value && format(new Date(this.form.get('day').value), 'yyyy-MM-dd')) || this.day;

        let duration = null;
        if (data.duration) {
            duration = this.toolsService.parseServiceDurationToInt(data.duration);
        }

        return {
            serviceId: this.getServiceFromServiceName(data.service)?.id,
            providerId: this.getIdFromProviderName(data.provider),
            duration,
            dateInit: this.getDateInit(day, data.startHour, data.startMinutes),
            providerSelected: data.providerSelected,
            id: data.id,
            roomEquipmentId: data.roomEquipment?.id,
        };
    }

    save() {
        // Guard against double submission: rapid double-clicks (or Enter + click) can dispatch
        // two submit events within the same task, before Angular disables the button, causing
        // duplicate bookings to be created milliseconds apart.
        if (this.saving) {
            return false;
        }

        this.errorMsg = null;
        this.submitAttempted = true;

        if (this.form.valid === false) {
            return false;
        }

        this.saving = true;

        const email = this.form.getRawValue().email === '' ? null : this.form.getRawValue().email;
        let phoneNumber = this.form.getRawValue().phoneNumber === '' ? null : this.form.getRawValue().phoneNumber;

        phoneNumber = this.parseFieldToPhoneNumber(phoneNumber);

        const fullName = this.form.get('client_name').value?.split(' ');
        const firstName = fullName[0];
        let lastName = '';
        if (fullName?.length > 1) {
            lastName = fullName.slice(1).join(' ');
        }

        const recurrenceString = this.form.get('recurrent').value;
        const recurrenceOption = this.recurrentOptions.find((option) => option.name === recurrenceString);
        const recurrenceValue = recurrenceOption?.value;

        const sendEmail = this.form.get('sendEmailCheck').value;
        const vatNumber = this.form.getRawValue().vatNumber;
        const unsubscribed = getUnsubscribedPayloadValue(this.form.getRawValue().subscribed, this.existingClient);
        let dob = this.form.getRawValue().dob;
        if (dob) {
            dob = format(dob, 'yyyy-MM-dd');
        }

        const bookings = this.form.get('services').value;
        const bookingsPayload = [];
        bookings.forEach((data) => {
            bookingsPayload.push(this.getBookingToPayload(data));
        });

        const payload: any = {
            bookingsPayload,
            notes: this.form.get('notes').value,
            firstName: firstName,
            lastName: lastName,
            email: email?.trim(),
            phoneNumber: phoneNumber,
            vatNumber,
            dob,
        };

        if (typeof unsubscribed === 'boolean') {
            payload.unsubscribed = unsubscribed;
        }

        if (this.merchantCustomFields?.length) {
            payload.customFields = {};
            this.form.get('customFields').value.forEach((field, index) => {
                let id = Object.keys(field)[0];
                const value: string = field[id];
                id = id.replace('_', '');
                payload.customFields[id] = value;
            });
        }

        if (!this.event) {
            payload['sendEmail'] = sendEmail;
            if (sendEmail && this.form.get('sendPaymentLinkCheck')?.value && this.canShowSendPaymentLinkOption()) {
                payload['sendPaymentLink'] = true;
            }
        }

        if (this.isRecurrent && recurrenceValue) {
            payload['recurrenceEndDate'] = this.form.get('recurrentUntil').value
                ? format(new Date(this.form.get('recurrentUntil').value), 'yyyy-MM-dd')
                : null;

            if (recurrenceValue.type === 'monthly_weekday') {
                payload['recurrenceFrequency'] = 'monthly_weekday';
                payload['recurrenceBySetPos'] = recurrenceValue.bySetPos;
            } else if (recurrenceValue.type === 'monthly_day') {
                payload['recurrenceFrequency'] = 'monthly_day';
                payload['recurrenceByMonthDay'] = recurrenceValue.byMonthDay;
            } else {
                payload['recurrenceFrequency'] = 'weekly';
                payload['recurrence'] = recurrenceValue.interval;
            }
        }

        if (this.existingClient) {
            payload['merchantClientId'] = this.existingClient.id;
        }

        if (this.event) {
            // UPDATE EVENT

            //if editing and date/time changed
            const originalStartDates = [];
            if (this.event.multipleBookings?.length) {
                this.event.multipleBookings.forEach((v) => {
                    originalStartDates.push(parse(v.dateInit, 'yyyy-MM-dd HH:mm', new Date()));
                });
            } else {
                originalStartDates.push(parse(this.event.dateInit, 'yyyy-MM-dd HH:mm', new Date()));
            }
            const newStartDates = bookingsPayload.map((v) => {
                return parse(v.dateInit, 'yyyy-MM-dd HH:mm', new Date());
            });

            const datesHaveChanges = originalStartDates.some((originalDate, index) => {
                if (!newStartDates[index]) {
                    return false;
                }
                return !isSameSecond(originalDate, newStartDates[index]);
            });

            if (datesHaveChanges) {
                const initialState = {
                    question: this.translate.instant('SHARED.createBookingModal.notifyClientConfirm'),
                };
                this.notifyUserModalRef = this.modalService.show(ConfirmModalComponent, { class: 'modal-md second', initialState });
                const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
                    payload['notifyClient'] = false;
                    if (reason && typeof reason === 'boolean') {
                        payload['notifyClient'] = reason;
                    }
                    this.editBooking(this.event.id, payload);
                    modalCallback.unsubscribe();
                });
                return;
            }

            this.editBooking(this.event.id, payload);
        } else {
            // CREATE EVENT
            this.storesService.createBookingAdmin(this.store.id, payload).subscribe(
                (res) => {
                    this.saving = false;

                    this.modalService.setDismissReason('saved');

                    this.bsModalRef.hide();
                    if (res?.totalBookingsCreatedOnBackoffice <= 5) {
                        this.analytics.trackProductAnalyticsEvent('createdBookings', {
                            totalBookingsCreatedOnBackoffice: res?.totalBookingsCreatedOnBackoffice,
                        });
                    }
                    if (res?.totalBookingsCreatedOnBackoffice >= 1) {
                        // > 1 to prevent edge case with multibooking
                        this.setSetupGuideStepCompleted(SetupGuideStepNames.CreateFirstAppointment);
                    }
                },
                (err) => {
                    this.errorMsg = err.errMsg;
                    this.saving = false;
                },
            );
        }
    }

    setSetupGuideStepCompleted(stepId: SetupGuideStepNames) {
        if (this.merchant?.settings?.showSetupGuide?.incomplete && this.merchant?.settings?.showSetupGuide?.[stepId] === true) {
            return;
        }
        if (!this.merchant?.settings?.showSetupGuide?.incomplete) {
            return;
        }
        const settings = { ...this.merchant.settings };
        if (!settings.showSetupGuide) {
            settings.showSetupGuide = {};
        }
        settings.showSetupGuide[stepId] = true;
        this.merchantsService.updateMerchant(this.merchant.id, { settings }).subscribe((res) => {
            this.analytics.trackProductAnalyticsEvent('setup_tour_step_complete', { stepId });
            this.merchantsService.activeMerchant.next(res);
        });
    }

    editBooking(eventId, payload) {
        this.storesService.editBooking(eventId, payload).subscribe(
            (res) => {
                this.saving = false;
                this.modalService.setDismissReason('saved');
                this.bsModalRef.hide();
            },
            (err) => {
                this.errorMsg = err.errMsg;
                this.saving = false;
            },
        );
    }

    noShowBooking(missed) {
        const payload = {
            noshow: missed,
        };
        this.storesService.noShowBooking(this.event.id, payload).subscribe(
            (res) => {
                this.saving = false;
                this.modalService.setDismissReason('saved');
                this.bsModalRef.hide();
            },
            (err) => {
                this.errorMsg = err.errMsg;
                this.saving = false;
            },
        );
    }

    deleteBooking() {
        this.modalService.setDismissReason('delete');
        this.bsModalRef.hide();
    }

    close() {
        this.modalService.setDismissReason(null);
        this.bsModalRef.hide();
    }

    checkPastDate() {
        const currentDay = this.form.get('day').value;
        if (currentDay) {
            if (isAfter(startOfDay(new Date()), startOfDay(new Date(currentDay)))) {
                return true;
            }
        }
        return false;
    }

    checkTodayOrPastDate() {
        const currentDay = this.form.get('day').value;
        if (currentDay) {
            if (isToday(new Date(currentDay)) || isAfter(startOfDay(new Date()), startOfDay(new Date(currentDay)))) {
                return true;
            }
        }
        return false;
    }

    checkPastTime(index) {
        const now = Date.now();
        const cache = this.pastTimeCache[index];
        if (cache && now - cache.timestamp < 1000) {
            return cache.value;
        }

        if (this.checkPastDate()) {
            this.pastTimeCache[index] = { value: false, timestamp: now };
            return false;
        }
        const currentDay = this.form.get('day').value;
        const currentTime = this.servicesFormArray.at(index).get('startHour').value && this.servicesFormArray.at(index).get('startMinutes').value;
        if (currentDay && currentTime) {
            new Date();

            const time = set(new Date(currentDay), {
                hours: Number(
                    format(parse(this.servicesFormArray.at(index).get('startHour').value, this.intlService.getTimeFormatHour(), new Date()), 'HH'),
                ),
                minutes: this.servicesFormArray.at(index).get('startMinutes').value,
            });

            if (isAfter(new Date(), time)) {
                this.pastTimeCache[index] = { value: true, timestamp: now };
                return true;
            }
        }

        this.pastTimeCache[index] = { value: false, timestamp: now };
        return false;
    }

    private outOfBusinessHoursCache: { [index: number]: { value: boolean; timestamp: number } } = {};
    private pastTimeCache: { [index: number]: { value: boolean; timestamp: number } } = {};

    checkOutOfBusinessHours(index) {
        const now = Date.now();
        const cache = this.outOfBusinessHoursCache[index];
        if (cache && now - cache.timestamp < 1000) {
            return cache.value;
        }

        let weekDay;
        if (this.day) {
            weekDay = format(parse(this.day, 'yyyy-MM-dd', new Date()), 'E');
        } else {
            weekDay = format(new Date(), 'E');
        }

        let storeWorkplan = this.store.workplan[weekDay.toLowerCase()];

        let providerOverrideWorkplan = null;
        if (this.currentWorkplanOverrides && Array.isArray(this.currentWorkplanOverrides)) {
            const providerName = this.servicesFormArray.at(index).get('provider').value;
            const providerId = this.getIdFromProviderName(providerName);
            const override = this.currentWorkplanOverrides.find((o) => o.providerId === providerId);
            if (override && override.workplan && override.workplan[weekDay.toLowerCase()]) {
                providerOverrideWorkplan = override.workplan[weekDay.toLowerCase()];
            }
        }

        const providerName = this.servicesFormArray.at(index).get('provider').value;
        let providerHasWonWorkplan = false;
        let providerWorkplan;
        if (providerName) {
            providerHasWonWorkplan = this.getProviderFromProviderName(providerName)?.workplan;
            providerWorkplan = providerHasWonWorkplan?.[weekDay.toLowerCase()];
        }

        let workplan;
        if (providerOverrideWorkplan) {
            workplan = providerOverrideWorkplan;
        } else if (providerWorkplan && providerWorkplan?.length > 0) {
            workplan = providerWorkplan;
        } else if (storeWorkplan && storeWorkplan?.length > 0) {
            workplan = storeWorkplan;
        }

        let result;
        if (workplan && workplan?.length > 0) {
            const serviceTime = set(new Date(), {
                hours: Number(
                    format(parse(this.servicesFormArray.at(index).get('startHour').value, this.intlService.getTimeFormatHour(), new Date()), 'HH'),
                ),
                minutes: this.servicesFormArray.at(index).get('startMinutes').value,
            });

            // Check if service time falls within any workplan time slot
            const isWithinAnyWorkplan = workplan.some((timeSlot) => {
                if (!timeSlot.start || !timeSlot.end) {
                    return false;
                }

                const startTime = timeSlot.start.split(':');
                const endTime = timeSlot.end.split(':');

                const workplanStart = set(new Date(), {
                    hours: parseInt(startTime[0]),
                    minutes: parseInt(startTime[1]),
                });
                const workplanEnd = set(new Date(), {
                    hours: parseInt(endTime[0]),
                    minutes: parseInt(endTime[1]),
                });

                // Check if service time is within this workplan slot
                return !isBefore(serviceTime, workplanStart) && isAfter(workplanEnd, serviceTime);
            });

            // Return true if service time is outside all workplan slots
            result = !isWithinAnyWorkplan;
        } else {
            // day off
            result = true;
        }

        this.outOfBusinessHoursCache[index] = { value: result, timestamp: now };
        return result;
    }

    toggleRecurrent(e) {
        if (this.isFreePlan()) {
            const initialState = {
                reason: 'recurrent',
            };
            this.upgradeModal = this.modalService.show(UpgradePlanModalComponent, { class: 'modal-md second', initialState });
            const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
                if (reason && reason === 'upgrade') {
                    this.close();
                }
                modalCallback.unsubscribe();
            });
            this.form.patchValue({ recurrentCheck: false });
            return false;
        }

        this.isRecurrent = !this.isRecurrent;
        if (this.isRecurrent) {
            this.buildRecurrentOptions(true);
        }
        this.recurrentValidation();
    }

    buildRecurrentOptions(preserveSelection = false) {
        const previousValue = preserveSelection
            ? this.recurrentOptions?.find((option) => option.name === this.form?.get('recurrent')?.value)?.value
            : null;

        const weeklyOptions = [1, 2, 3, 4, 5, 6, 7, 8].map((interval) => ({
            value: { type: 'weekly', interval },
            name:
                interval === 1
                    ? this.translate.instant('SHARED.createBookingModal.everyWeek')
                    : this.translate.instant('SHARED.createBookingModal.everyNWeek', { N: interval }),
        }));

        const dayValue = this.form?.get('day')?.value;
        const referenceDate = dayValue ? new Date(dayValue) : this.minDate || new Date();
        const monthlyOptions = this.buildMonthlyRecurrenceOptions(referenceDate);

        this.recurrentOptions = [...weeklyOptions, ...monthlyOptions];

        if (!this.form?.get('recurrent')) {
            return;
        }

        let nextOption = this.recurrentOptions[0];
        if (previousValue) {
            const matched = this.recurrentOptions.find((option) => {
                if (option.value.type !== previousValue.type) {
                    return false;
                }
                if (option.value.type === 'weekly') {
                    return option.value.interval === previousValue.interval;
                }
                if (option.value.type === 'monthly_day') {
                    return option.value.byMonthDay === previousValue.byMonthDay;
                }
                return option.value.bySetPos === previousValue.bySetPos;
            });
            if (matched) {
                nextOption = matched;
            } else if (previousValue.type === 'monthly_day' || previousValue.type === 'monthly_weekday') {
                // Keep a monthly frequency when the date's ordinal/day no longer matches
                // (e.g. 1st Monday → 3rd Monday). Only fall back to weekly if the prior choice was weekly.
                nextOption =
                    this.recurrentOptions.find((option) => option.value.type === previousValue.type) ||
                    this.recurrentOptions.find(
                        (option) => option.value.type === 'monthly_day' || option.value.type === 'monthly_weekday'
                    ) ||
                    nextOption;
            }
        }

        this.form.patchValue({ recurrent: nextOption.name }, { emitEvent: false });
    }

    buildMonthlyRecurrenceOptions(referenceDate: Date) {
        const dayOfMonth = referenceDate.getDate();
        const options = [
            {
                value: { type: 'monthly_day', byMonthDay: dayOfMonth },
                name: this.translate.instant('SHARED.createBookingModal.monthlyOnDay', {
                    day: dayOfMonth,
                }),
            },
            ...this.buildMonthlyWeekdayRecurrenceOptions(referenceDate),
        ];

        return options;
    }

    buildMonthlyWeekdayRecurrenceOptions(referenceDate: Date) {
        const { weekday, nth, isLast } = this.getWeekdayOccurrenceInMonth(referenceDate);
        const weekdayLabel = this.translate.instant(`SHARED.createBookingModal.recurrenceWeekday.${weekday}`);
        const feminine = this.isFeminineRecurrenceWeekday(weekday);
        const ordinalKey = feminine
            ? 'SHARED.createBookingModal.recurrenceOrdinal'
            : 'SHARED.createBookingModal.recurrenceOrdinalMasculine';
        const nthKey = feminine
            ? 'SHARED.createBookingModal.monthlyOnNthWeekday'
            : 'SHARED.createBookingModal.monthlyOnNthWeekdayMasculine';
        const lastKey = feminine
            ? 'SHARED.createBookingModal.monthlyOnLastWeekday'
            : 'SHARED.createBookingModal.monthlyOnLastWeekdayMasculine';
        const options = [];

        if (nth >= 1 && nth <= 4) {
            const ordinal = this.translate.instant(`${ordinalKey}.${nth}`);
            options.push({
                value: { type: 'monthly_weekday', bySetPos: nth },
                name: this.translate.instant(nthKey, {
                    ordinal,
                    weekday: weekdayLabel,
                }),
            });
        }

        if (isLast) {
            options.push({
                value: { type: 'monthly_weekday', bySetPos: -1 },
                name: this.translate.instant(lastKey, {
                    weekday: weekdayLabel,
                }),
            });
        }

        return options;
    }

    /**
     * PT: *feira weekdays are feminine; domingo and sábado are masculine.
     * EN/ES use matching F/M keys with identical copy.
     */
    isFeminineRecurrenceWeekday(weekday: number) {
        return weekday >= 1 && weekday <= 5;
    }

    getWeekdayOccurrenceInMonth(date: Date) {
        const weekday = date.getDay();
        const dayOfMonth = date.getDate();
        const nth = Math.ceil(dayOfMonth / 7);
        const nextWeek = new Date(date);
        nextWeek.setDate(dayOfMonth + 7);
        const isLast = nextWeek.getMonth() !== date.getMonth();

        return { weekday, nth, isLast };
    }

    recurrentValidation() {
        if (this.isRecurrent) {
            this.form.controls['recurrentUntil'].setValidators([Validators.required]);
            this.form.controls['recurrentUntil'].updateValueAndValidity();
        } else {
            this.form.controls['recurrentUntil'].setValidators([]);
            this.form.controls['recurrentUntil'].updateValueAndValidity();
        }
    }

    isFreePlan() {
        return this.merchant.plan === 'free' || this.merchant.plan === null || this.merchant.plan === 'canceled';
    }

    toggleSendEmail(e) {
        this.sendEmail = !this.sendEmail;
        localStorage?.setItem('book:settings:sendEmail', JSON.stringify({ value: this.sendEmail }));
    }

    onEventDateChange(value: Date, manualUpdate: boolean = false): void {
        if (manualUpdate) {
            this.form.patchValue({ day: value });
            this.form.updateValueAndValidity();
        }
        // generate times available for this day
        if (!this.firstTimeLoadedDatePicker) {
            this.day = format(value, 'yyyy-MM-dd');
            this.openTimes = this.generateAvailableTimesForSelectedDay(true);

            this.form.updateValueAndValidity();
        }
        this.firstTimeLoadedDatePicker = false;

        // update storeWorkplan if overriden
        const dateCheckOverride = startOfDay(value);
        const availabilityPayload = {
            dateInit: format(dateCheckOverride, 'yyyy-MM-dd HH:mm'),
            dateEnd: format(endOfDay(dateCheckOverride), 'yyyy-MM-dd HH:mm'),
            providerId: null,
        };
        this.currentWorkplanOverrides = null;

        this.storesService.getStoreCalendar(this.store?.id, availabilityPayload).subscribe((availability) => {
            if (availability.length > 0 && availability[0].workplanOverride.length > 0) {
                this.currentWorkplanOverrides = availability[0].workplanOverride;
            }
        });
    }

    /**
     * Edit / Save footers for this modal. Admins and other roles always see actions (subject to template).
     * Providers blocked via {@link isProviderBlockedFromBookings} never do. Otherwise provider/manager
     * follow the same rules as before: optional past-date/time booking when store flags allow.
     */
    canShowBookingEditAndSubmitActions(): boolean {
        if (this.user?.role === 'provider' && this.store?.allowProvidersToCreateBookings === false) {
            return false;
        }

        if (this.user?.role !== 'provider' && this.user?.role !== 'manager') {
            return true;
        }

        if (this.user?.role === 'provider' && this.store?.allowProvidersToCreateBookingsInThePast) {
            return true;
        }

        if (this.user?.role === 'manager' && this.store?.allowManagersToCreateBookingsInThePast) {
            return true;
        }

        return !this.checkPastDate() && !this.checkPastTime(0);
    }

    checkCanDeleteBooking(): boolean {
        if (this.user?.role === 'provider' && !this.store.allowProvidersToCancelBookings) {
            return false;
        }
        return true;
    }

    editSale(sale: any) {
        this.router.navigateByUrl('/' + this.store?.slug + `/dashboard/payment/edit/${sale.id}`);
        return;
    }

    createSale(sale?: any) {
        let multiBookingParentId = null;
        let eventToAdd = null;

        if (this.event.multipleBookings?.length) {
            multiBookingParentId = this.event.bookingPackageId;
            eventToAdd = this.event.multipleBookings;
        } else {
            eventToAdd = [this.event];
        }
        this.invoicingService.addBookingsToCart(eventToAdd, this.event.client, multiBookingParentId, () => {
            this.goToCreateSale();
        });
        return;
    }

    addInvoice(sale?: any) {
        this.router.navigateByUrl('/' + this.store?.slug + `/dashboard/payment/complete/${sale.id}`);
    }

    addPayment(sale: any) {
        this.router.navigateByUrl('/' + this.store?.slug + `/dashboard/payment/pay/${sale.id}`);
    }

    createLegacyInvoice() {
        const initialState = {
            currentStore: this.store,
            merchant: this.merchant,
            client: {
                email: this.form.getRawValue().email === '' ? null : this.form.getRawValue().email,
                phoneNumber: this.form.getRawValue().phoneNumber === '' ? null : this.form.getRawValue().phoneNumber,
                fullName: this.form.get('client_name').value,
                vatNumber: this.form.get('vatNumber').value,
                bookingId: this.event.id,
            },
            bookingServices: this.event.multipleBookings ? this.event.multipleBookings.map((v) => v.service) : [this.event.service],
        };

        this.invoiceModal = this.modalService.show(CreateInvoiceModalComponent, { initialState, class: 'second' });

        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            modalCallback.unsubscribe();
        });
    }

    paywithTeya() {
        const ref = this.dialog.open(SimplePaymentModalComponent, {
            data: {
                bookingId: this.event.id,
                amount: this.missingPaymentCents,
            },
        });
        ref.closed.subscribe((result: any) => {
            if (result === 'success') {
                this.initializeModal();
            }
        });
    }

    /**
     * Terminal pay (no sales module): hide when the linked sale is already fully paid.
     * Keep visible for unpaid 0€ bookings (no sale / no money collected yet).
     */
    canShowBookingTeyaPayButton(): boolean {
        if (this.hasLegacyPosIntegration() || this.teyaConnectionStatus()?.active !== true) {
            return false;
        }

        const sale = this.bookingSale();
        const paidAmount = sale?.paidAmountCents ?? (sale?.paidAmount != null ? Math.round(sale.paidAmount * 100) : 0);

        if (sale?.status === ISaleStatus.PAID && paidAmount > 0) {
            return false;
        }

        return true;
    }

    goToCreateSale() {
        this.router.navigateByUrl('/' + this.store?.slug + '/dashboard/payment/create');
    }

    seeSaleDetails(sale: any) {
        this.modalService.show(SaleDetailsComponent, {initialState: { sale }, class: 'modal-lg second'});
        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            if (reason && reason === 'updated') {
                this.getBookingSales();
            }
            modalCallback.unsubscribe();
        });
    }

    hasDropdownOptions(): boolean {
        // Check if there are any options to show in the dropdown menu
        const hasNoShowOption = this.checkTodayOrPastDate();
        const hasDeleteOption = this.hasLegacyPosIntegration() && this.checkCanDeleteBooking();
        return hasNoShowOption || hasDeleteOption;
    }

    openWhatsApp(phoneNumber: string) {
        if (!phoneNumber) {
        return;
        }
        // Format phone number for WhatsApp: remove all non-digit characters
        // wa.me expects only digits (country code + number, no + sign)
        let formattedNumber = phoneNumber.trim().replace(/\D/g, '');
        if (!formattedNumber) {
        return;
        }
        const whatsappUrl = `https://wa.me/${formattedNumber}`;
        window.open(whatsappUrl, '_blank');
    }

    openEmail(email: string) {
        if (!email) {
        return;
        }
        window.open(`mailto:${email}`, '_blank');
    }

    openPhone(phoneNumber: string) {
        if (!phoneNumber) {
        return;
        }
        window.open(`tel:${phoneNumber}`, '_blank');
    }

    openVideoCall(): void {
        if (this.event?.videoCallUrl) {
            window.open(this.event.videoCallUrl, '_blank', 'noopener,noreferrer');
        }
    }

    canCreateSale() {
        return this.store?.salesModuleActive && this.userService.userHasPermissions('canCreateEditSales');
    }

    canViewSaleDetails() {
        return this.store?.salesModuleActive && this.userService.userHasPermissions('canViewSales');
    }
}
